import { createContext, useContext, useMemo, type ReactNode } from 'react'
import useAuthStore from '@/store/useAuthStore'
import useGetBillingSubscription, {
  type PlanFeature,
} from '@/services/api/billing/useGetBillingSubscription'

interface FeatureGateContextValue {
  /** lookup_key -> feature descriptor for the current team's plan catalogue */
  features: Record<string, PlanFeature>
  /** false until the billing/subscription query has resolved at least once */
  loaded: boolean
  hasActiveSubscription: boolean
}

const FeatureGateContext = createContext<FeatureGateContextValue>({
  features: {},
  loaded: false,
  hasActiveSubscription: false,
})

export function FeatureGateProvider({ children }: { children: ReactNode }) {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data, isSuccess } = useGetBillingSubscription({ teamId: currentTeamId })

  const value = useMemo<FeatureGateContextValue>(() => {
    const features: Record<string, PlanFeature> = {}
    for (const feature of data?.features ?? []) {
      features[feature.lookup_key] = feature
    }
    return {
      features,
      loaded: isSuccess,
      hasActiveSubscription: data?.has_active_subscription ?? false,
    }
  }, [data, isSuccess])

  return (
    <FeatureGateContext.Provider value={value}>
      {children}
    </FeatureGateContext.Provider>
  )
}

export function useFeatureGateContext() {
  return useContext(FeatureGateContext)
}
